<template>
  <v-container fluid>
    <v-row v-if="sistemas.length">
      <v-col>
        <b-card class="mr-2">
          <InputText
            type="text"
            v-model="filtroSistemas"
            placeholder="Pesquisar por nome do sistema"
            class="mr-3"
            style="width: 300px"
          />
          <MultiSelect
            v-model="contasSelecionadas"
            :options="contas"
            option-label="conta_nome"
            option-value="conta_id"
            placeholder="Selecione as contas"
            :filter="true"
            style="width: 200px"
            class="mr-3"
          />
          <MultiSelect
            v-model="tiposSelecionados"
            :options="tiposDosSistemas"
            style="width: 200px"
            placeholder="Selecione os tipos"
            :filter="true"
          />
        </b-card>
      </v-col>
    </v-row>
    <v-row dense fill-height>
      <v-col
        v-for="sistema in sistemasFiltrados"
        :key="sistema.sistema_id"
        cols="12"
        sm="6"
        md="3"
        class="d-flex"
      >
        <div
          class="card-wrapper d-flex flex-grow-1"
          :class="{ 'expanded-wrapper': sistema.mostrarTodos }"
        >
          <b-card
            class="overflow-hidden d-flex flex-column flex-grow-1 card-transition mb-2 mr-2"
            :class="{ 'expanded-card': sistema.mostrarTodos }"
          >
            <v-img
              :src="sistema.sis_logo_url"
              height="180"
              contain
              class="d-flex align-center justify-center m-1"
            ></v-img>

            <v-card-text class="d-flex flex-column flex-grow-1">
              <h2 class="text-h6 font-weight-medium mb-3">
                {{ sistema.sis_nome }}
              </h2>

              <div class="mb-0">
                <v-chip
                  v-for="banco in sistema.bancos"
                  :key="'banco-' + banco.sistema_banco_id"
                  class="ma-1 mr-1 mb-1"
                  color="blue lighten-5"
                  text-color="blue darken-2"
                  small
                >
                  <v-icon left small>{{ banco.icon }}</v-icon>
                  {{ banco.banco_id }} ({{ banco.clientes.length }})
                </v-chip>
              </div>

              <div class="mt-0">
                <div
                  class="d-flex align-center justify-space-between mb-2 mt-2"
                >
                  <div class="text-caption font-weight-medium pt-2">
                    Clientes ({{ clientesUnicos(sistema).length }})
                  </div>
                  <v-btn
                    v-if="clientesUnicos(sistema).length > 4"
                    text
                    small
                    color="primary"
                    @click="sistema.mostrarTodos = !sistema.mostrarTodos"
                  >
                    {{ sistema.mostrarTodos ? "Mostrar Menos" : "Ver Todos" }}
                  </v-btn>
                </div>

                <div v-if="clientesUnicos(sistema).length">
                  <v-chip
                    v-for="cliente in clientesLimitados(sistema)"
                    :key="cliente.conta_id"
                    class="ma-1 mr-1 mb-1"
                    color="green lighten-5"
                    text-color="green darken-2"
                    small
                  >
                    {{ cliente.conta_nome }}
                    <small class="ml-1 grey--text text-caption">
                      [{{ cliente.bancos.join(", ") }}]
                    </small>
                  </v-chip>
                </div>

                <div v-else class="text-caption grey--text">
                  Nenhum cliente registrado.
                </div>
              </div>
            </v-card-text>
          </b-card>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import ApiService from "@/core/services/api.service";
import MultiSelect from "primevue/multiselect";
import InputText from "primevue/inputtext";

export default {
  name: "SistemasIntegrados",
  components: {
    MultiSelect,
    InputText
  },
  data() {
    return {
      loading: false,
      filtroSistemas: "",
      sistemas: [],
      contasSelecionadas: [],
      tiposSelecionados: []
    };
  },
  computed: {
    contas() {
      const contasMap = new Map();

      this.sistemas.forEach((sistema) => {
        sistema.bancos.forEach((banco) => {
          banco.clientes.forEach((cliente) => {
            // Evita duplicatas usando o conta_id como chave
            if (!contasMap.has(cliente.conta_id)) {
              contasMap.set(cliente.conta_id, {
                conta_id: cliente.conta_id,
                conta_nome: `${cliente.conta_id} - ${cliente.conta_nome}`
              });
            }
          });
        });
      });

      return Array.from(contasMap.values());
    },

    tiposDosSistemas() {
      const tiposSet = new Set();

      this.sistemas.forEach((sistema) => {
        sistema.bancos.forEach((banco) => {
          banco.mt_tipos.forEach((tipo) => {
            tiposSet.add(tipo);
          });
        });
      });

      return Array.from(tiposSet).sort(); // opcional: ordena alfabeticamente
    },

    sistemasFiltrados() {
      const filterByNome = this.filtroSistemas
        ? this.sistemas.filter((sistema) => {
            return sistema.sis_nome
              .toLowerCase()
              .includes(this.filtroSistemas.toLowerCase());
          })
        : this.sistemas;

      const filterByContas = this.contasSelecionadas.length
        ? filterByNome.filter((sistema) => {
            return sistema.bancos.some((banco) => {
              return banco.clientes.some(
                (c) =>
                  c.conta_id && this.contasSelecionadas.includes(c.conta_id)
              );
            });
          })
        : filterByNome;

      const filterByTipos = this.tiposSelecionados.length
        ? filterByContas.filter((sistema) => {
            return sistema.bancos.some((banco) => {
              return banco.mt_tipos.some((tipo) => {
                return this.tiposSelecionados.includes(tipo);
              });
            });
          })
        : filterByContas;

      return filterByTipos;
    }
  },
  methods: {
    fetchSistemas() {
      this.loading = true;

      setTimeout(async () => {
        const response = await ApiService.get(
          "/administrativo/sistema/integracoes"
        );
        if (response.data.status === "success") {
          this.sistemas = response.data.data.sistemas.map((sistema) => ({
            ...sistema,
            mostrarTodos: false // controla exibição completa
          }));
        } else {
          await this.$store.dispatch("setAlert", {
            type: "error",
            message: response.data.message
          });
        }
        this.loading = false;
      }, 1000);
    },
    clientesUnicos(sistema) {
      const mapaClientes = new Map();

      sistema.bancos.forEach((banco) => {
        banco.clientes.forEach((cliente) => {
          if (!mapaClientes.has(cliente.conta_id)) {
            mapaClientes.set(cliente.conta_id, {
              conta_id: cliente.conta_id,
              conta_nome: cliente.conta_nome,
              bancos: [banco.banco_id]
            });
          } else {
            mapaClientes.get(cliente.conta_id).bancos.push(banco.banco_id);
          }
        });
      });

      return Array.from(mapaClientes.values());
    },

    clientesLimitados(sistema) {
      const todosClientes = this.clientesUnicos(sistema);

      return sistema.mostrarTodos ? todosClientes : todosClientes.slice(0, 4);
    }
  },
  mounted() {
    this.fetchSistemas();
  }
};
</script>

<style scoped>
.v-card {
  transition: all 0.2s ease-in-out;
}

.v-card:hover {
  transform: scale(1.01);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.card-wrapper {
  z-index: 1;
  position: relative;
  width: 100%;
  max-height: 450px;
  min-height: 350px;
  display: flex;
}

.expanded-wrapper {
  z-index: 2;
}

.card-transition {
  transition: all 0.3s ease-in-out;
}

.expanded-card {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: auto;
  width: 100%;
  transform: scale(1.05);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
  background-color: white;
  max-height: none; /* para permitir que o conteúdo cresça naturalmente */
}
</style>
